import React from "react";
import { DailyIcon } from "@/features/daily/components/DailyIcon";
import { DailyTip } from "@/features/daily/components/DailyTip";
import { useDailyNow } from "@/features/daily/hooks/use-daily-now";
import { CauseCountsVm, EventVm, StatusPillVm, healthViewModel } from "@/features/daily/components/command/health-view-model";

type Props = {
    fetchedAt: number | null;
    auto: boolean;
    onToggleAuto: () => void;
    pollSeconds: number;
    /** `null` until the first status arrives. */
    pill: StatusPillVm | null;
    counts: CauseCountsVm | null;
    events: EventVm[] | null;
    eventsFailed: boolean;
    onOpenEvents: (trigger: HTMLElement) => void;
};

const madridClock = new Intl.DateTimeFormat("es-ES", {
    timeZone: "Europe/Madrid",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hourCycle: "h23",
});

const emptyEventText = (events: EventVm[] | null, failed: boolean): string => {
    if (failed) return "Eventos no disponibles";
    if (events === null) return "Cargando eventos…";
    return "Sin eventos en 24 h";
};

const partOf = (parts: Intl.DateTimeFormatPart[], type: Intl.DateTimeFormatPartTypes): string => {
    const part = parts.find((piece) => piece.type === type);
    return part === undefined ? "" : part.value;
};

/**
 * D125 + vb-metrics#38 U5: the live bar of Salud — refresh age and auto-refresh, the state
 * chip, the latest observed event (it opens the latest events) and the Madrid clock. Not a
 * live region: nothing here is announced every second; the seconds are hidden from assistive technology.
 */
export const LiveBar: React.FC<Props> = ({ fetchedAt, auto, onToggleAuto, pollSeconds, pill, counts, events, eventsFailed, onOpenEvents }) => {
    const now = useDailyNow(1000);
    const clock = madridClock.formatToParts(new Date(now));
    const latest = events === null || events.length === 0 ? null : events[0];
    return (
        <div className="daily-live daily-cc-live" role="group" aria-label="Estado en vivo">
            <span className="daily-live__badge" data-live={auto}>
                <span className="daily-dot" aria-hidden="true" />
                {auto ? "EN VIVO" : "EN PAUSA"}
            </span>
            <span className="daily-live__age">Último refresco {healthViewModel.refreshedAgo(fetchedAt, now)}</span>
            {/* Guardian P3: a toggle keeps one name; `aria-pressed` and the icon carry the state. */}
            <button type="button" className="daily-live__toggle" aria-pressed={auto} title={`Cada ${pollSeconds} s`} onClick={onToggleAuto}>
                <DailyIcon name={auto ? "autorenew" : "pause"} /> Actualización automática
            </button>
            {pill === null ? null : (
                <span className="daily-pill daily-live__state" data-tone={pill.tone}>
                    <span className="daily-dot" aria-hidden="true" />
                    {pill.label}
                    <DailyTip id="daily-live-state-tip" label="Detalle del estado" lines={pill.tooltip} />
                </span>
            )}
            {counts === null || counts.critical === 0 ? null : (
                <span className="daily-pill" data-tone="critical">CRÍTICO {counts.critical}</span>
            )}
            {counts === null || counts.warning === 0 ? null : (
                <span className="daily-pill" data-tone="warning">AVISO {counts.warning}</span>
            )}
            <button type="button" className="daily-cc-last" aria-haspopup="dialog" onClick={(event) => onOpenEvents(event.currentTarget)}>
                <span className="daily-cc-last__label">Último evento</span>
                {latest === null ? (
                    <span className="daily-cc-last__detail">{emptyEventText(events, eventsFailed)}</span>
                ) : (
                    <>
                        <time className="daily-cc-last__time" dateTime={new Date(latest.at).toISOString()}>{latest.timeLabel}</time>
                        <span className="daily-cc-last__title" data-tone={latest.tone}>{latest.title}</span>
                        <span className="daily-cc-last__detail">{latest.detail}</span>
                    </>
                )}
                <span className="daily-cc-last__more">Ver últimos <DailyIcon name="chevron_right" /></span>
            </button>
            <span className="daily-live__clock">
                <span className="daily-live__zone">MAD</span>
                <time className="daily-live__time">
                    {partOf(clock, "hour")}:{partOf(clock, "minute")}
                    <span aria-hidden="true">:{partOf(clock, "second")}</span>
                </time>
            </span>
        </div>
    );
};
